import { useState, useEffect } from "react";
import { Guidebook } from "../types/guidebook";
import { paths, API_URL } from "../config/endpoints";
import { refreshToken, isTokenExpired } from "../services/authServices";

export const useGuidebook = (guidebookUid: string, unitUid: string) => {
  const [guidebook, setGuidebook] = useState<Guidebook | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchGuidebook = async () => {
      setLoading(true);
      setError(null);

      try {
        let token = localStorage.getItem("token");

        if (token && isTokenExpired(token)) {
          try {
            token = await refreshToken();
          } catch (refreshError) {
            token = null;
          }
        }

        if (!token) {
          throw new Error("Unauthorized: Invalid authentication credentials");
        }

        const headers: HeadersInit = {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        };

        const apiUrl = unitUid
          ? `${API_URL}/${paths.guest}/${paths.guidebooks}/${guidebookUid}/${paths.units}/${unitUid}`
          : `${API_URL}/${paths.guest}/${paths.guidebook}/${guidebookUid}`;
        const response = await fetch(apiUrl, {
          method: "GET",
          headers,
        });

        if (!response.ok) {
          if (response.status === 404) {
            throw new Error("Guidebook not found");
          } else if (response.status === 401) {
            throw new Error("Unauthorized: Invalid authentication credentials");
          } else if (response.status === 403) {
            throw new Error("Forbidden: You don't have access to this guidebook");
          } else {
            throw new Error("Failed to fetch guidebook");
          }
        }

        const data = await response.json();
        setGuidebook(data);
      } catch (error: any) {
        setError(error.message || "An error occurred while fetching guidebook");
      } finally {
        setLoading(false);
      }
    };

    if (guidebookUid) {
      fetchGuidebook();
    } else {
      setGuidebook(null);
      setLoading(false);
    }
  }, [guidebookUid, unitUid]);

  return { guidebook, loading, error };
};
